import { Api, BASE_URL, BOTS_BASE_URL } from '@/api/index';
import axios, { AxiosRequestConfig } from 'axios';
import { GoogleMyBusinessAccount, GoogleMyBusinessLocation } from 'ignite360-core';

export interface GoogleBusinessFeedItem {
  id: string;
  title: string;
  content: string;
  mediaType: string;
  thumbnailUrl: string | null;
  permalink?: string | null;
  /** Google post lifecycle: LIVE, PROCESSING, REJECTED, SCHEDULED, … */
  state?: string | null;
}

export class GoogleMyBusinessApi {
  static async loadAccounts(authTokenId: string): Promise<GoogleMyBusinessAccount[]> {
    const config: AxiosRequestConfig = {
      method: 'GET',
      url: `${BASE_URL}/public/google-my-business/accounts?auth_token_id=${authTokenId}`,
    };

    const result = await axios.request(config);

    if (result.status === 200 && result.data) {
      return result.data as GoogleMyBusinessAccount[];
    }

    throw new Error(result.data.error);
  }

  static async loadLocations(
    authTokenId: string,
    accountId: string,
  ): Promise<GoogleMyBusinessLocation[]> {
    const config: AxiosRequestConfig = {
      method: 'GET',
      url: `${BASE_URL}/public/google-my-business/locations?auth_token_id=${authTokenId}&account_id=${accountId}`,
    };

    const result = await axios.request(config);

    if (result.status === 200) {
      return result.data as GoogleMyBusinessLocation[];
    }

    throw new Error(result.data.error);
  }

  static async publishTestPost(botId: string, accountId: string, locationId: string) {
    const config: AxiosRequestConfig = {
      method: 'POST',
      url: `${BASE_URL}/public/bots/${botId}/google-my-business/publish-listing`,
      data: { accountId, locationId },
    };

    const res = await axios.request(config);

    if (res.status !== 204) {
      throw new Error('Something went wrong while trying to post on GoogleMyBusiness.');
    }
  }

  static async connectBotWithGoogleMyBusiness(
    botId: string,
    authTokenId: string,
    accountId: string,
    locationId: string,
  ): Promise<void> {
    const config: AxiosRequestConfig = {
      method: 'POST',
      url: `${BASE_URL}/public/bots/${botId}/google-my-business/subscribe`,
      data: { authTokenId, accountId, locationId },
    };

    const res = await axios.request(config);

    if (res.status === 200) {
      return;
    }

    throw new Error(
      res.data.error ||
        'Something went wrong while trying to connect your GoogleMyBusiness location.',
    );
  }

  /** Unwraps nested proxy URLs down to the original Google/media URL. */
  static normalizeMediaUrl(url: string): string {
    let current = (url || '').trim();
    for (let depth = 0; depth < 3; depth++) {
      if (!current.includes('/google-my-business/proxy-media')) {
        break;
      }
      try {
        const parsed = new URL(current);
        const inner = parsed.searchParams.get('url');
        if (!inner) {
          break;
        }
        current = decodeURIComponent(inner);
      } catch {
        break;
      }
    }
    return current;
  }

  /** Proxies Google Business media through the backend to avoid browser hotlink blocks. */
  static buildProxyMediaUrl(botId: string, rawUrl: string): string | null {
    const normalized = GoogleMyBusinessApi.normalizeMediaUrl(rawUrl);
    if (!normalized || !/^https?:\/\//i.test(normalized)) {
      return null;
    }
    if (normalized.includes('/google-my-business/proxy-media')) {
      return normalized;
    }
    return `${BASE_URL}/public/bots/${botId}/google-my-business/proxy-media?url=${encodeURIComponent(
      normalized,
    )}`;
  }

  /**
   * Downloads Google Business post media by local post id (authenticated API route).
   */
  static async downloadMediaFile(
    botId: string,
    localPostId: string,
  ): Promise<{ file: File; kind: 'image' | 'video' }> {
    const config = Api.finalizeConfig({
      method: 'GET',
      url: `${BOTS_BASE_URL}/${botId}/google-my-business/local-posts/${encodeURIComponent(
        localPostId,
      )}/media/file`,
      responseType: 'arraybuffer',
    });
    const result = await axios.request<ArrayBuffer>(config);
    if (result.status !== 200 || !result.data) {
      throw new Error('Could not download media.');
    }
    const contentType = (result.headers['content-type'] as string) || 'application/octet-stream';
    const exposeKind = result.headers['x-google-business-media-kind'] as string | undefined;
    const nameMatch = /filename\*=UTF-8''([^;]+)|filename="([^"]+)"/.exec(
      (result.headers['content-disposition'] as string) || '',
    );
    const rawName = nameMatch ? decodeURIComponent(nameMatch[1] || nameMatch[2]) : '';
    const treatAsVideo = exposeKind === 'video' || contentType.startsWith('video/');
    const kind: 'image' | 'video' = treatAsVideo ? 'video' : 'image';
    const fileName =
      rawName || `google-business-${localPostId}${kind === 'video' ? '.mp4' : '.jpg'}`;
    const blob = new Blob([result.data], { type: contentType });
    return { file: new File([blob], fileName, { type: contentType }), kind };
  }

  static async getRecentPosts(botId: string): Promise<GoogleBusinessFeedItem[]> {
    const config = Api.finalizeConfig({
      method: 'GET',
      url: `${BOTS_BASE_URL}/${botId}/google-my-business/recent-posts`,
    });
    const result = await axios.request<GoogleBusinessFeedItem[]>(config);
    if (result.status === 200 && result.data) {
      return result.data;
    }
    return [];
  }

  /** Permanently deletes a Google Business local post on the connected location. */
  static async deletePost(botId: string, localPostId: string): Promise<void> {
    const config = Api.finalizeConfig({
      method: 'DELETE',
      url: `${BOTS_BASE_URL}/${botId}/google-my-business/local-posts/${encodeURIComponent(
        localPostId,
      )}`,
    });
    const result = await axios.request(config);
    if (result.status === 204) {
      return;
    }
    throw new Error('Could not delete Google Business post.');
  }

  static async disconnectBotFromGoogleBusiness(botId: string): Promise<void> {
    const config: AxiosRequestConfig = {
      method: 'POST',
      url: `${BASE_URL}/public/bots/${botId}/google-my-business/unsubscribe`,
    };
    const res = await axios.request(config);
    if (res.status === 204) {
      return;
    }

    throw new Error(
      res.data.error ||
        'Something went wrong while trying to disconnect your GoogleMyBusiness location.',
    );
  }
}
